/**
 * `/capture?title=&text=&url=` — the PWA manifest's `share_target` and the
 * "Capture" app shortcut (W4-E). Pure helpers for `capture.tsx`; the `-`
 * prefix keeps this file out of the generated route tree.
 */

export interface CaptureShareSearch {
  title?: string;
  text?: string;
  url?: string;
}

/** Capture titles are ≤ 500 characters (`CaptureSchema`). */
export const SHARE_TEXT_MAX = 500;

function clean(v: unknown): string | undefined {
  if (typeof v !== 'string') return undefined;
  const s = v.replace(/\s+/g, ' ').trim();
  return s ? s.slice(0, 2000) : undefined;
}

/** Only web links are taken as the shared URL. */
function webUrl(v: unknown): string | undefined {
  const s = clean(v);
  if (!s) return undefined;
  try {
    const u = new URL(s);
    return u.protocol === 'https:' || u.protocol === 'http:' ? u.href : undefined;
  } catch {
    return undefined;
  }
}

/** Route `validateSearch`: tolerant — unknown or bad params are dropped. */
export function parseCaptureShare(raw: Record<string, unknown>): CaptureShareSearch {
  const out: CaptureShareSearch = {};
  const title = clean(raw.title);
  const text = clean(raw.text);
  const url = webUrl(raw.url);
  if (title) out.title = title;
  if (text) out.text = text;
  if (url) out.url = url;
  return out;
}

/**
 * One capture line from what was shared: title, then text, then the link —
 * skipping a part another part already contains (apps often repeat the title
 * in the text, or the link in both).
 */
export function shareToCaptureText(s: CaptureShareSearch): string {
  const parts: string[] = [];
  for (const candidate of [s.title, s.text, s.url]) {
    if (!candidate) continue;
    if (parts.some((p) => p.includes(candidate))) continue;
    for (let i = parts.length - 1; i >= 0; i--) if (candidate.includes(parts[i]!)) parts.splice(i, 1);
    parts.push(candidate);
  }
  const text = parts.join(' ');
  if (text.length <= SHARE_TEXT_MAX) return text;
  // Too long: shorten the words, never the link (a cut link is useless).
  const link = parts.at(-1) === s.url ? s.url : undefined;
  if (link && link.length < SHARE_TEXT_MAX / 2) {
    const head = parts.slice(0, -1).join(' ');
    return `${head.slice(0, SHARE_TEXT_MAX - link.length - 2)}… ${link}`;
  }
  return `${text.slice(0, SHARE_TEXT_MAX - 1)}…`;
}
